import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { FileBarChart, Plus, Star, Trash2, UserPlus, Car, ExternalLink, Search, Edit2, Paperclip, Download, X, MessageCircle, Send, Loader2, BarChart3, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const TIPO_VEICOLO = ['Autovettura', 'Motociclo', 'Ciclomotore', 'Autocarro', 'Altro senza targa'];
const RAMI = ['RCA Auto', 'RCA Moto', 'Vita', 'Incendio', 'Furto e Incendio', 'Kasko', 'Infortuni', 'Salute', 'RC Professionale', 'RC Generale', 'Multirischi Casa', 'Viaggio', 'Altro'];
const STATI = { in_attesa: { label: 'In Attesa', color: 'bg-amber-50 text-amber-700 border-amber-200' }, confermato: { label: 'Confermato', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' }, rifiutato: { label: 'Rifiutato', color: 'bg-red-50 text-red-700 border-red-200' } };
const emptyForm = {
  nome_cliente: '', cognome_cliente: '', targa: '', tipo_veicolo: 'Autovettura',
  compagnia_attuale: '', compagnia_proposta: '', importo: '', note: '',
  telefono: '', email_cliente: '', ramo: 'RCA Auto',
  pagamento_attuale: '', pagamento_proposto: '',
  garanzie_attuali: '', garanzie_proposte: ''
};

export const Preventivi = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [preventivi, setPreventivi] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrev, setEditingPrev] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState('miei');
  const [sendingWaId, setSendingWaId] = useState(null);
  const [showCharts, setShowCharts] = useState(true);
  const [statusFilter, setStatusFilter] = useState('tutti');

  useEffect(() => { fetchAll(); }, []);

  // Auto-open dialog if URL params from Scadenze
  useEffect(() => {
    const nome = searchParams.get('nome');
    if (nome && !loading) {
      const parts = nome.trim().split(' ');
      const cognome = parts.length > 1 ? parts.slice(1).join(' ') : '';
      const firstName = parts[0] || '';
      setEditingPrev(null);
      setForm({
        ...emptyForm,
        nome_cliente: firstName,
        cognome_cliente: cognome,
        targa: searchParams.get('targa') || '',
        compagnia_attuale: searchParams.get('compagnia') || '',
        telefono: searchParams.get('cellulare') || '',
      });
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  const fetchAll = async () => {
    try {
      const [pRes, uRes] = await Promise.all([api.get('/preventivi'), api.get('/users')]);
      setPreventivi(pRes.data);
      setUsers(uRes.data);
    } catch {} finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingPrev(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingPrev(p);
    setForm({
      nome_cliente: p.nome_cliente || '', cognome_cliente: p.cognome_cliente || '',
      targa: p.targa || '', tipo_veicolo: p.tipo_veicolo || 'Autovettura',
      compagnia_attuale: p.compagnia_attuale || p.compagnia || '',
      compagnia_proposta: p.compagnia_proposta || '',
      importo: p.importo || '', note: p.note || '',
      telefono: p.telefono || '', email_cliente: p.email_cliente || '',
      ramo: p.ramo || 'RCA Auto',
      pagamento_attuale: p.pagamento_attuale || '',
      pagamento_proposto: p.pagamento_proposto || '',
      garanzie_attuali: p.garanzie_attuali || '',
      garanzie_proposte: p.garanzie_proposte || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome_cliente || !form.cognome_cliente) { toast.error('Nome e cognome obbligatori'); return; }
    try {
      if (editingPrev) {
        await api.patch(`/preventivi/${editingPrev.id}`, form);
        toast.success('Preventivo aggiornato');
      } else {
        await api.post('/preventivi', form);
        toast.success('Preventivo creato');
      }
      setDialogOpen(false);
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleUploadAllegato = async (prevId, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/preventivi/${prevId}/allegati`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Allegato caricato');
      fetchAll();
    } catch { toast.error('Errore upload allegato'); }
    finally { setUploading(false); }
  };

  const handleDeleteAllegato = async (prevId, allegatoId) => {
    try {
      await api.delete(`/preventivi/${prevId}/allegati/${allegatoId}`);
      toast.success('Allegato rimosso');
      fetchAll();
    } catch {}
  };

  const toggleMigliore = async (prev) => {
    try { await api.patch(`/preventivi/${prev.id}`, { migliore: !prev.migliore }); fetchAll(); } catch {}
  };

  const changeStato = async (prev, stato) => {
    try { await api.patch(`/preventivi/${prev.id}`, { stato }); fetchAll(); } catch {}
  };

  const assignTo = async (prev, userId) => {
    try { await api.patch(`/preventivi/${prev.id}`, { assigned_to: userId }); toast.success('Assegnato'); fetchAll(); } catch {}
  };

  const deletePrev = async (id) => {
    if (!window.confirm('Eliminare questo preventivo?')) return;
    try { await api.delete(`/preventivi/${id}`); fetchAll(); } catch {}
  };

  const sendPrevWhatsApp = async (p) => {
    if (!p.telefono) { toast.error('Nessun numero di telefono per questo cliente'); return; }
    setSendingWaId(p.id);
    try {
      const num = p.telefono.replace(/[^0-9+]/g, '').replace('+', '');
      const lines = [
        `Gentile ${p.nome_cliente} ${p.cognome_cliente},`,
        `le inviamo il dettaglio del preventivo richiesto:`,
        '',
      ];
      if (p.ramo) lines.push(`Ramo: ${p.ramo}`);
      if (p.compagnia_proposta) lines.push(`Compagnia: ${p.compagnia_proposta}`);
      if (p.garanzie_proposte) lines.push(`Garanzie: ${p.garanzie_proposte}`);
      if (p.importo) lines.push(`Importo: ${p.importo} EUR`);
      if (p.targa) lines.push(`Targa: ${p.targa}`);
      if (p.pagamento_proposto) lines.push(`Pagamento: ${p.pagamento_proposto}`);
      lines.push('', 'Per qualsiasi chiarimento non esiti a contattarci.', 'F.lli Schiliro Assicurazioni');
      const msg = lines.join('\n');
      navigate(`/whatsapp?numero=${encodeURIComponent(num)}&messaggio=${encodeURIComponent(msg)}`);
    } catch { toast.error('Errore invio'); }
    finally { setSendingWaId(null); }
  };

  // Filtering: by tab (user), status, and search
  const filtered = preventivi.filter(p => {
    // Tab filter (user)
    if (tab === 'miei' && p.created_by !== user?.id) return false;
    if (tab !== 'miei' && tab !== 'tutti') {
      if (p.created_by !== tab) return false;
    }
    // Status filter
    if (statusFilter !== 'tutti' && p.stato !== statusFilter) return false;
    // Search filter
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return `${p.nome_cliente} ${p.cognome_cliente}`.toLowerCase().includes(q) || p.targa?.toLowerCase().includes(q);
  });

  // Get unique creators for tabs
  const creatorMap = {};
  preventivi.forEach(p => {
    if (p.created_by && p.created_by !== user?.id) {
      creatorMap[p.created_by] = p.created_by_nome || 'Utente';
    }
  });
  const myCount = preventivi.filter(p => p.created_by === user?.id).length;

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="p-5 md:p-7 min-h-screen" data-testid="preventivi-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Preventivi</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} preventivi {tab === 'miei' ? 'fatti da me' : tab === 'tutti' ? 'totali' : ''}</p>
        </div>
        <div className="flex gap-2">
          <a href="https://www.preventivass.it/" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="text-xs" data-testid="open-preventivass">
              <ExternalLink size={13} className="mr-1.5" /> Preventivass
            </Button>
          </a>
          <Button onClick={openCreate} data-testid="new-preventivo-btn">
            <Plus size={16} className="mr-2" /> Nuovo Preventivo
          </Button>
        </div>
      </div>

      {/* Tab per utente */}
      <div className="flex gap-1 mb-3 bg-slate-100 p-1 rounded-lg w-fit flex-wrap">
        <button onClick={() => setTab('miei')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'miei' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-miei">
          I Miei ({myCount})
        </button>
        <button onClick={() => setTab('tutti')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'tutti' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-tutti">
          Tutti ({preventivi.length})
        </button>
        {Object.entries(creatorMap).map(([uid, nome]) => (
          <button key={uid} onClick={() => setTab(uid)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === uid ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-user-${uid}`}>
            {nome} ({preventivi.filter(p => p.created_by === uid).length})
          </button>
        ))}
      </div>

      {/* Filtro per stato */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs font-semibold text-slate-400 uppercase mr-1">Stato:</span>
        {[
          { key: 'tutti', label: 'Tutti', color: 'text-slate-700', activeBg: 'bg-slate-900 text-white' },
          { key: 'in_attesa', label: 'In Attesa', color: 'text-amber-700', activeBg: 'bg-amber-500 text-white' },
          { key: 'confermato', label: 'Confermati', color: 'text-emerald-700', activeBg: 'bg-emerald-500 text-white' },
          { key: 'rifiutato', label: 'Rifiutati', color: 'text-red-600', activeBg: 'bg-red-500 text-white' },
        ].map(s => {
          const count = s.key === 'tutti' ? preventivi.length : preventivi.filter(p => p.stato === s.key).length;
          return (
            <button key={s.key} onClick={() => setStatusFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                statusFilter === s.key ? s.activeBg : `bg-white border border-slate-200 ${s.color} hover:border-slate-300`
              }`}
              data-testid={`status-filter-${s.key}`}>
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      {/* ─── ANALYTICS SECTION ─── */}
      <div className="mb-5">
        <button onClick={() => setShowCharts(!showCharts)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3 transition-colors"
          data-testid="toggle-charts">
          <BarChart3 size={16} />
          <span>Statistiche Preventivi</span>
          {showCharts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showCharts && (() => {
          // Build analytics data
          const PIE_COLORS = ['#f59e0b', '#10b981', '#ef4444'];
          const BAR_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

          // Stats cards
          const totalPrev = preventivi.length;
          const inAttesa = preventivi.filter(p => p.stato === 'in_attesa').length;
          const confermati = preventivi.filter(p => p.stato === 'confermato').length;
          const rifiutati = preventivi.filter(p => p.stato === 'rifiutato').length;

          // Per-user data
          const userMap = {};
          preventivi.forEach(p => {
            const uid = p.created_by || 'unknown';
            const name = p.created_by_nome || 'Sconosciuto';
            if (!userMap[uid]) userMap[uid] = { name, totale: 0, confermati: 0, in_attesa: 0, rifiutati: 0 };
            userMap[uid].totale++;
            if (p.stato === 'confermato') userMap[uid].confermati++;
            else if (p.stato === 'rifiutato') userMap[uid].rifiutati++;
            else userMap[uid].in_attesa++;
          });
          const barData = Object.values(userMap).sort((a, b) => b.totale - a.totale);

          // Pie data
          const pieData = [
            { name: 'In Attesa', value: inAttesa },
            { name: 'Confermati', value: confermati },
            { name: 'Rifiutati', value: rifiutati },
          ].filter(d => d.value > 0);

          // Conversion rate
          const convRate = totalPrev > 0 ? Math.round((confermati / totalPrev) * 100) : 0;

          return (
            <div className="space-y-4">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-[11px] text-slate-500 font-medium">Totale Preventivi</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{totalPrev}</p>
                </div>
                <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
                  <p className="text-[11px] text-amber-600 font-medium">In Attesa</p>
                  <p className="text-2xl font-bold text-amber-700 mt-1">{inAttesa}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
                  <p className="text-[11px] text-emerald-600 font-medium">Confermati</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">{confermati}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-[11px] text-slate-500 font-medium flex items-center gap-1"><TrendingUp size={11} /> Tasso Conversione</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{convRate}%</p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Bar Chart - Preventivi per Collega */}
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="chart-per-user">
                  <h3 className="text-sm font-bold text-slate-900 mb-4">Preventivi per Collega</h3>
                  {barData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                          formatter={(v, name) => [v, name === 'confermati' ? 'Confermati' : name === 'in_attesa' ? 'In Attesa' : name === 'rifiutati' ? 'Rifiutati' : name]} />
                        <Bar dataKey="confermati" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} name="Confermati" />
                        <Bar dataKey="in_attesa" stackId="a" fill="#f59e0b" name="In Attesa" />
                        <Bar dataKey="rifiutati" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} name="Rifiutati" />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-slate-400 text-center py-8">Nessun dato</p>}
                </div>

                {/* Pie Chart - Stato Preventivi */}
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="chart-stato">
                  <h3 className="text-sm font-bold text-slate-900 mb-4">Stato Preventivi</h3>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                          {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-slate-400 text-center py-8">Nessun dato</p>}
                </div>
              </div>

              {/* Ranking Table */}
              {barData.length > 1 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ranking-table">
                  <div className="px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-bold text-slate-900">Classifica Colleghi</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50/50 border-b border-slate-100">
                        <th className="text-left px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">#</th>
                        <th className="text-left px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">Collega</th>
                        <th className="text-center px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">Totale</th>
                        <th className="text-center px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">Confermati</th>
                        <th className="text-center px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">In Attesa</th>
                        <th className="text-center px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">Rifiutati</th>
                        <th className="text-center px-5 py-2 text-[11px] text-slate-500 font-semibold uppercase">% Conv.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {barData.map((d, i) => (
                        <tr key={`${d.name}-${i}`} className="hover:bg-slate-50/50">
                          <td className="px-5 py-2.5 font-bold text-slate-400">{i + 1}</td>
                          <td className="px-5 py-2.5 font-semibold text-slate-800">{d.name}</td>
                          <td className="px-5 py-2.5 text-center font-medium">{d.totale}</td>
                          <td className="px-5 py-2.5 text-center"><span className="text-emerald-600 font-semibold">{d.confermati}</span></td>
                          <td className="px-5 py-2.5 text-center"><span className="text-amber-600 font-semibold">{d.in_attesa}</span></td>
                          <td className="px-5 py-2.5 text-center"><span className="text-red-500 font-semibold">{d.rifiutati}</span></td>
                          <td className="px-5 py-2.5 text-center">
                            <span className={`font-bold ${d.totale > 0 && d.confermati / d.totale >= 0.5 ? 'text-emerald-600' : 'text-slate-500'}`}>
                              {d.totale > 0 ? Math.round((d.confermati / d.totale) * 100) : 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <div className="mb-4 relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Cerca per nome o targa..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="pl-9" data-testid="preventivi-search" />
      </div>

      <div className="space-y-3">
        {filtered.map(p => {
          const stato = STATI[p.stato] || STATI.in_attesa;
          const allegati = p.allegati || [];
          return (
            <div key={p.id} className={`bg-white rounded-xl border ${p.migliore ? 'border-amber-300 shadow-sm' : 'border-slate-200'} p-4`} data-testid={`prev-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {p.migliore && <Star size={14} className="text-amber-500 fill-amber-500" />}
                    <h3 className="text-sm font-semibold text-slate-900">{p.nome_cliente} {p.cognome_cliente}</h3>
                    <Badge variant="outline" className={`text-[10px] ${stato.color}`}>{stato.label}</Badge>
                    {p.ramo && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-600 border-indigo-200">{p.ramo}</Badge>}
                    {p.assigned_to_nome && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">{p.assigned_to_nome}</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                    {p.targa && <span className="flex items-center gap-1"><Car size={12} /> {p.targa}</span>}
                    <span>{p.tipo_veicolo}</span>
                    {p.importo && <span className="font-bold text-emerald-600">&euro; {p.importo}</span>}
                    {p.telefono && <span>{p.telefono}</span>}
                  </div>
                  {/* Compagnie e Pagamento */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] mt-1.5">
                    {(p.compagnia_attuale || p.compagnia) && <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Da: {p.compagnia_attuale || p.compagnia}</span>}
                    {p.compagnia_proposta && <span className="bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">Proposta: {p.compagnia_proposta}</span>}
                    {p.pagamento_attuale && <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Paga: {p.pagamento_attuale}</span>}
                    {p.pagamento_proposto && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Propongo: {p.pagamento_proposto}</span>}
                  </div>
                  {/* Garanzie */}
                  {(p.garanzie_attuali || p.garanzie_proposte) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] mt-1">
                      {p.garanzie_attuali && <span className="text-slate-500"><strong>Garanzie attuali:</strong> {p.garanzie_attuali}</span>}
                      {p.garanzie_proposte && <span className="text-blue-600"><strong>Garanzie proposte:</strong> {p.garanzie_proposte}</span>}
                    </div>
                  )}
                  {p.note && <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">{p.note}</p>}
                  {/* Allegati */}
                  {allegati.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {allegati.map(a => (
                        <div key={a.id} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-[11px]">
                          <Paperclip size={10} className="text-slate-400" />
                          <span className="text-slate-600 max-w-[120px] truncate">{a.filename}</span>
                          <span className="text-slate-400">{formatSize(a.size)}</span>
                          <a href={`/api${a.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700"><Download size={10} /></a>
                          <button onClick={() => handleDeleteAllegato(p.id, a.id)} className="text-red-400 hover:text-red-600"><X size={10} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                    <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">Salvato da {p.created_by_nome}</span>
                    <span className="text-slate-400">{p.created_at ? format(new Date(p.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                  {p.telefono && (
                    <Button size="sm" variant="outline" className="h-7 text-[10px] text-emerald-600 border-emerald-200 hover:bg-emerald-50 gap-1"
                      onClick={() => sendPrevWhatsApp(p)} disabled={sendingWaId === p.id} data-testid={`wa-prev-${p.id}`}>
                      {sendingWaId === p.id ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
                      WhatsApp
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-blue-600" onClick={() => openEdit(p)} data-testid={`edit-prev-${p.id}`}>
                    <Edit2 size={13} />
                  </Button>
                  <Button size="sm" variant={p.migliore ? "default" : "outline"} className={`h-7 text-[10px] ${p.migliore ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                    onClick={() => toggleMigliore(p)} data-testid={`toggle-best-${p.id}`}>
                    <Star size={12} className={p.migliore ? 'fill-white' : ''} />
                  </Button>
                  <Select onValueChange={(v) => changeStato(p, v)} value={p.stato}>
                    <SelectTrigger className="h-7 w-[110px] text-[10px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_attesa">In Attesa</SelectItem>
                      <SelectItem value="confermato">Confermato</SelectItem>
                      <SelectItem value="rifiutato">Rifiutato</SelectItem>
                    </SelectContent>
                  </Select>
                  {isAdmin && (
                    <Select onValueChange={(v) => assignTo(p, v)} value={p.assigned_to || ''}>
                      <SelectTrigger className="h-7 w-[130px] text-[10px]"><UserPlus size={10} className="mr-1" /><SelectValue placeholder="Assegna" /></SelectTrigger>
                      <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <label className="cursor-pointer" data-testid={`upload-allegato-${p.id}`}>
                    <input type="file" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUploadAllegato(p.id, e.target.files[0]); e.target.value=''; }} disabled={uploading} />
                    <div className="h-7 w-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-300 transition-colors">
                      <Paperclip size={13} />
                    </div>
                  </label>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => deletePrev(p.id)}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && !loading && (
          <div className="text-center py-16">
            <FileBarChart size={32} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nessun preventivo</p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPrev ? 'Modifica Preventivo' : 'Nuovo Preventivo'}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nome</Label><Input value={form.nome_cliente} onChange={(e) => F('nome_cliente', e.target.value)} placeholder="Nome" data-testid="prev-nome" /></div>
              <div><Label className="text-xs">Cognome</Label><Input value={form.cognome_cliente} onChange={(e) => F('cognome_cliente', e.target.value)} placeholder="Cognome" data-testid="prev-cognome" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Telefono</Label><Input value={form.telefono} onChange={(e) => F('telefono', e.target.value)} placeholder="+39..." data-testid="prev-tel" /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.email_cliente} onChange={(e) => F('email_cliente', e.target.value)} placeholder="email@..." data-testid="prev-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Targa</Label><Input value={form.targa} onChange={(e) => F('targa', e.target.value.toUpperCase())} placeholder="AA000BB" data-testid="prev-targa" /></div>
              <div>
                <Label className="text-xs">Tipo Veicolo</Label>
                <Select value={form.tipo_veicolo} onValueChange={(v) => F('tipo_veicolo', v)}>
                  <SelectTrigger data-testid="prev-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPO_VEICOLO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Ramo Assicurativo</Label>
              <Select value={form.ramo} onValueChange={(v) => F('ramo', v)}>
                <SelectTrigger data-testid="prev-ramo"><SelectValue placeholder="Seleziona ramo" /></SelectTrigger>
                <SelectContent>{RAMI.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Separator />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Situazione Attuale del Cliente</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Compagnia Attuale</Label><Input value={form.compagnia_attuale} onChange={(e) => F('compagnia_attuale', e.target.value)} placeholder="Es. Generali, Zurich..." data-testid="prev-comp-attuale" /></div>
              <div>
                <Label className="text-xs">Pagamento Attuale</Label>
                <Select value={form.pagamento_attuale} onValueChange={(v) => F('pagamento_attuale', v)}>
                  <SelectTrigger data-testid="prev-pag-attuale"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Annuale">Annuale</SelectItem>
                    <SelectItem value="Semestrale">Semestrale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Garanzie Attuali</Label><Textarea value={form.garanzie_attuali} onChange={(e) => F('garanzie_attuali', e.target.value)} placeholder="RC, Furto, Incendio, Kasko..." rows={2} data-testid="prev-gar-attuali" /></div>
            <Separator />
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Proposta</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Compagnia Proposta</Label><Input value={form.compagnia_proposta} onChange={(e) => F('compagnia_proposta', e.target.value)} placeholder="Es. Allianz, UnipolSai..." data-testid="prev-comp-proposta" /></div>
              <div>
                <Label className="text-xs">Pagamento Proposto</Label>
                <Select value={form.pagamento_proposto} onValueChange={(v) => F('pagamento_proposto', v)}>
                  <SelectTrigger data-testid="prev-pag-proposto"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Annuale">Annuale</SelectItem>
                    <SelectItem value="Semestrale">Semestrale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Garanzie Proposte</Label><Textarea value={form.garanzie_proposte} onChange={(e) => F('garanzie_proposte', e.target.value)} placeholder="RC, Furto, Incendio, Kasko, Cristalli..." rows={2} data-testid="prev-gar-proposte" /></div>
            <Separator />
            <div><Label className="text-xs">Importo Proposto</Label><Input value={form.importo} onChange={(e) => F('importo', e.target.value)} placeholder="350.00" data-testid="prev-importo" /></div>
            <div><Label className="text-xs">Note</Label><Textarea value={form.note} onChange={(e) => F('note', e.target.value)} placeholder="Note aggiuntive..." rows={2} data-testid="prev-note" /></div>
            <Button onClick={handleSave} className="w-full" data-testid="create-prev-btn">
              {editingPrev ? 'Aggiorna Preventivo' : 'Crea Preventivo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
